import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, ShieldCheck, Package, HardHat, Sofa, BadgeCheck } from "lucide-react";
import HeroArt from "@/components/site/HeroArt";

const line = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.7, 0, 0.3, 1], delay: 0.3 + i * 0.11 },
  }),
};

const trust = [
  { icon: ShieldCheck, label: "GeM Registered" },
  { icon: BadgeCheck, label: "MSME Registered" },
  { icon: Package, label: "Procurement Solutions" },
  { icon: HardHat, label: "Project Execution" },
  { icon: Sofa, label: "Interior Design" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const artScale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[112vh] w-full overflow-hidden bg-[var(--nv-paper)] text-[var(--nv-navy)]"
    >
      {/* Vertical rules */}
      <div className="absolute inset-y-0 left-6 md:left-10 w-px bg-[var(--nv-line)]" aria-hidden />
      <div className="absolute inset-y-0 right-6 md:right-10 w-px bg-[var(--nv-line)]" aria-hidden />

      {/* Composition — parallax */}
      <motion.div
        style={{ y: artY, scale: artScale, transformOrigin: "60% 40%" }}
        className="absolute inset-y-0 right-0 w-full md:w-[58%] lg:w-[56%] pointer-events-none"
      >
        <HeroArt />
      </motion.div>

      {/* Soft left-side gradient so text always reads on white */}
      <div
        className="absolute inset-y-0 left-0 w-full md:w-[62%] pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, var(--nv-paper) 0%, var(--nv-paper) 46%, rgba(245,241,234,0.55) 74%, rgba(245,241,234,0) 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 overline text-[var(--nv-gold)]"
          data-testid="hero-overline"
        >
          <span className="inline-block w-8 h-px bg-[var(--nv-gold)]" />
          <span>Nsoul Ventures · Business Solutions</span>
        </motion.div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 mt-14 md:mt-20">
        <motion.h1
          initial="hidden"
          animate="show"
          className="display text-[11vw] md:text-[6.6vw] lg:text-[5.9vw] leading-[0.94] max-w-[980px]"
          data-testid="hero-heading"
        >
          <span className="mask-line block"><motion.span variants={line} custom={0} className="block">Business Solutions,</motion.span></span>
          <span className="mask-line block"><motion.span variants={line} custom={1} className="block">Engineered with</motion.span></span>
          <span className="mask-line block"><motion.span variants={line} custom={2} className="block italic text-[var(--nv-gold)]">Precision.</motion.span></span>
        </motion.h1>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 mt-14 md:mt-20 grid grid-cols-12 gap-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="col-span-12 md:col-span-6 lg:col-span-5 text-[15px] md:text-[17px] leading-[1.7] text-[var(--nv-navy)]/70 max-w-xl"
          data-testid="hero-description"
        >
          Procurement solutions, supply, interior design, and project execution — delivered with
          professionalism, reliability, and long-term value.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="col-span-12 md:col-span-6 lg:col-span-5 lg:col-start-8 flex flex-wrap gap-3 md:justify-start"
        >
          <a href="#contact" className="btn-gold" data-testid="hero-cta-contact">
            Get in Touch <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <a href="#services" className="btn-ghost" data-testid="hero-cta-services">
            Explore Services
          </a>
        </motion.div>
      </div>

      {/* Trust indicators strip */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.4 }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-24"
        data-testid="hero-trust-strip"
      >
        <div className="flex flex-wrap items-center gap-x-10 md:gap-x-14 gap-y-4 pt-8 border-t border-[var(--nv-line)] max-w-3xl">
          {trust.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.label} className="flex items-center gap-3" data-testid={`trust-strip-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
                <Icon className="w-4 h-4 text-[var(--nv-gold)]" strokeWidth={1.6} />
                <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--nv-navy)]/75">
                  {t.label}
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Bottom meta row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        className="absolute z-10 bottom-10 left-0 right-0"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3 overline text-[var(--nv-navy)]/55" data-testid="hero-scroll-hint">
            <ArrowDown className="w-3.5 h-3.5animate-none" />
            <span>Scroll to explore</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
